import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Button, Card, ConfirmDialog, DataGrid, SearchableSelect, useToast, v,
  type DataGridColumn, type SearchableSelectOption, type Sanitizer, type Validator,
} from '@kss/ui';
import { Plus, RefreshCw, Trash2, Pencil, Save, ArrowLeft } from 'lucide-react';
import { StandardAddDropdown } from './AddDropdown';
import { useModulePerms } from '../../hooks/useModulePerms';
import { encodeId, decodeId } from '../../utils/idCodec';
import ImagePicker from '../../components/ImagePicker';
import NumberField from '../../components/NumberField';

/**
 * Minimal service contract required by CrudPage.
 */
export interface CrudService<T, Create, Filter> {
  all: () => Promise<T[]>;
  get: (id: string) => Promise<T>;
  find?: (f: Filter) => Promise<T>;
  filter?: (f: Filter) => Promise<T[]>;
  create: (items: Create[]) => Promise<T[]>;
  update: (items: T[]) => Promise<T[]>;
  delete: (id: string) => Promise<void>;
}

export interface CrudField<F extends object> {
  key: keyof F & string;
  label: string;
  type?: 'text' | 'email' | 'tel' | 'url' | 'number' | 'date' | 'time' | 'textarea' | 'select' | 'status' | 'search' | 'image' | 'custom';
  required?: boolean;
  placeholder?: string;
  options?: { value: string; label: string }[];
  /** For `type: 'search'` — async loader called on first open. Cached thereafter. */
  loadOptions?: () => Promise<SearchableSelectOption[]>;
  /** For `type: 'search'` — let user commit raw text that doesn't match any option. */
  allowCustom?: boolean;
  fullWidth?: boolean;
  render?: (value: F[keyof F & string], setValue: (v: F[keyof F & string]) => void, form: F) => ReactNode;
  /**
   * Optional cross-field autofill hook. Fires after this field's value
   * changes; return a partial form patch to update sibling fields.
   * Only invoked on user-initiated edits — not on initial mount, edit
   * hydration, or programmatic `setForm` from another field's onChange.
   * Ref: SSO Issue #10 (payment form autofill customer + amount from
   * selected invoice).
   */
  onChange?: (value: F[keyof F & string], nextForm: F) => Partial<F> | void;

  /* ---- Validation (see `rulesFor` below) ------------------------- */
  /**
   * Extra rules appended to the ones derived from `type` and `required`.
   * Use for constraints the field type can't imply — a duplicate check
   * against loaded rows, a cross-field date comparison, a domain range.
   */
  rules?: ReadonlyArray<Validator>;
  /** Overrides the sanitiser derived from `type`. */
  sanitize?: Sanitizer;
  /** Max characters. Enforced while typing AND on paste, and validated. */
  maxLength?: number;
  minLength?: number;
  /** Numeric bounds for `type: 'number'`. */
  min?: number;
  max?: number;
  /** `type: 'number'` — decimal places allowed. 0 = integers only. */
  scale?: number;
  /** Opt out of validation entirely for a field that manages its own. */
  noValidate?: boolean;
}

/**
 * Rules for a field, derived from its declared `type` and `required`.
 *
 * This is what makes Phase 3 a single-file change: all 44 screens built
 * on `CrudForm` already declare `type` and `required`, so every one of
 * them gains format, length and injection validation without editing a
 * single call site. `field.rules` extends this per-field where a type
 * can't express the constraint.
 *
 * Free-text fields get the SQL + XSS guards; `select`/`status`/`image`
 * do not, because their values are ids and booleans the user never
 * types. `textarea` keeps the guards but skips the space rules — a
 * description legitimately contains double spaces and line breaks.
 */
function rulesFor<F extends object>(fld: CrudField<F>): Validator[] {
  if (fld.noValidate) return [];
  const label = fld.label;
  const out: Validator[] = [];

  if (fld.required) {
    out.push(
      fld.type === 'select' || fld.type === 'search'
        ? v.requiredSelection(label)
        : v.required(label),
    );
  }
  if (fld.minLength != null) out.push(v.minLength(fld.minLength, label));
  if (fld.maxLength != null) out.push(v.maxLength(fld.maxLength, label));

  switch (fld.type) {
    case 'email':
      out.push(v.email(label));
      break;
    case 'tel':
      out.push(v.mobile({ label }));
      break;
    case 'url':
      out.push(v.url(label));
      break;
    case 'date':
      out.push(v.date({ label }));
      break;
    case 'time':
      out.push(v.time(label));
      break;
    case 'number':
      out.push(fld.scale === 0 ? v.integer(label) : v.decimal(fld.scale ?? 2, label));
      if (fld.min != null) out.push(v.min(fld.min, label));
      if (fld.max != null) out.push(v.max(fld.max, label));
      break;
    case 'textarea':
      out.push(...v.safeText(label));
      break;
    case 'select':
    case 'status':
    case 'search':
    case 'image':
    case 'custom':
      // Value is an id / boolean / URL the user does not free-type.
      break;
    default:
      // Plain text: hygiene + injection guards.
      out.push(v.noEdgeSpaces(label), v.noRepeatedSpaces(label), ...v.safeText(label));
      break;
  }

  if (fld.rules?.length) out.push(...fld.rules);
  return out;
}

/**
 * Keystroke/paste transform for a field. Returning `undefined` leaves
 * the value untouched.
 *
 * `tel` and `number` are reshaped so the control physically cannot hold
 * an invalid character — the brief's "enforce maximum character limits
 * while typing" and "validate pasted values the same as typed values".
 * Because this runs inside `onChange`, paste goes through it too.
 */
function sanitizerFor<F extends object>(fld: CrudField<F>): Sanitizer | undefined {
  if (fld.noValidate) return undefined;
  if (fld.sanitize) return fld.sanitize;
  const cap = fld.maxLength != null ? [v.capLength(fld.maxLength)] : [];
  switch (fld.type) {
    case 'tel':
      // 15 = ITU-T E.164 maximum, so a country code still fits.
      return v.chain(v.digitsOnly(fld.maxLength ?? 15));
    case 'number':
      return v.chain(v.decimalInput(fld.scale ?? 2), ...cap);
    case 'textarea':
      return cap.length ? v.chain(...cap) : undefined;
    case 'select':
    case 'status':
    case 'search':
    case 'image':
    case 'custom':
    case 'date':
    case 'time':
      return undefined;
    default:
      return v.chain(v.collapseSpaces, ...cap);
  }
}

export interface CrudPageListProps<T extends object, Create, Filter> {
  title: string;
  newLabel?: string;
  service: CrudService<T, Create, Filter>;
  idKey: keyof T & string;
  labelKey: keyof T & string;
  basePath: string;
  columns: DataGridColumn<T>[];
  /**
   * Backend module name for permission checks (e.g. "Product"). When omitted
   * we infer it from `basePath` — pass explicitly when the URL slug doesn't
   * map to the backend (e.g. "tenant-domain-maps" → "TenantDomainMap").
   */
  module?: string;
  /**
   * Initial sort applied on first render of the grid. Chronological grids
   * (invoices, payments, order-returns, …) should pass e.g.
   * `{ columnAccessor: 'invoiceDate', direction: 'desc' }` so newest rows
   * land on page 1. The user can still re-sort by clicking a header.
   * Ref: ORDER_WORKFLOW_AUDIT Issue 6.
   */
  initialSortStatus?: { columnAccessor: keyof T & string; direction: 'asc' | 'desc' };
}

export function CrudList<T extends object, Create, Filter>(
  props: CrudPageListProps<T, Create, Filter> & { bulkPath?: boolean }
) {
  const { title, newLabel, service, idKey, labelKey, basePath, columns, bulkPath = true, module, initialSortStatus } = props;
  const moduleName = module ?? inferModuleFromPath(basePath);
  const perms = useModulePerms(moduleName);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;

  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<T | null>(null);
  const [deleting, setDeleting] = useState(false);
  /* Row selection — mandatory across every CrudList grid per spec. */
  const [selectedIds, setSelectedIds] = useState<(string | number)[]>([]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { const data = await service.all(); setRows(Array.isArray(data) ? data : []); }
    catch (e) { const msg = e instanceof Error ? e.message : 'Failed to load'; setError(msg); toastRef.current.error('Load failed', msg); }
    finally { setLoading(false); }
  }, [service]);
  useEffect(() => { load(); }, [load]);

  const handleDelete = async () => {
    const id = confirmDelete?.[idKey] as string | undefined;
    if (!id) return;
    setDeleting(true);
    try {
      await service.delete(id);
      toast.success('Deleted', `${String(confirmDelete![labelKey] ?? 'record')} removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  const withActionsCol: DataGridColumn<T>[] = [
    ...columns,
    ...(perms.canUpdate || perms.canDelete
      ? [{
          accessor: idKey,
          title: 'Actions',
          width: 110,
          render: (r: T) => (
            <div className="flex items-center gap-1">
              {perms.canUpdate && (
                <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
                  onClick={(e) => { e.stopPropagation(); const id = r[idKey]; if (id) navigate(`${basePath}/${encodeId(String(id))}`); }} title="Edit"><Pencil className="h-3.5 w-3.5" /></button>
              )}
              {perms.canDelete && (
                <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                  onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete"><Trash2 className="h-3.5 w-3.5" /></button>
              )}
            </div>
          ),
        } as DataGridColumn<T>]
      : []),
  ];

  // Whole-grid gate — if the JWT carries neither `.List` nor `.Read`
  // for this module, render a "not permitted" alert instead of firing
  // service.all() and showing an empty grid. Admin bypass in
  // useModulePerms means Administrator / SuperAdmin / TenantAdmin /
  // Owner always resolve `canList = true`, so this only ever hides the
  // grid for genuinely-unprivileged roles.
  if (!perms.canList && !perms.canRead) {
    return (
      <div className="-m-6 p-[3px]">
        <Alert variant="warning">
          You don't have permission to view {title.toLowerCase()}.
        </Alert>
      </div>
    );
  }

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      <DataGrid<T>
        title={<span className="hidden sm:inline">{title}</span>}
        rowId={idKey}
        columnPicker data={rows} columns={withActionsCol}
        /* Row-select checkboxes are mandatory across every CrudList
           grid (spec: "checkboxes are missing in multiple grids and
           should be mandatory"). Bug ref: AI Agent Issue #10b. */
        selectable
        selectedIds={selectedIds}
        onSelectedChange={setSelectedIds}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        initialSortStatus={initialSortStatus}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            {/* Empty `newLabel=""` is the explicit "read-only entity" signal —
                used for derived ledgers that have no insert form. Without
                this gate the "+" button still renders and routes to a
                missing `/new` page, which the catchall <Navigate to="/" />
                resolves as an unwanted redirect to the storefront root.
                Bug ref: AI Agent Issue #7 (Accounting → Ledger). */}
            {newLabel === '' ? null : bulkPath ? (
              <StandardAddDropdown
                basePath={basePath}
                newLabel={newLabel ?? 'New'}
                service={service}
                module={moduleName}
                templateFields={columns
                  .map((c) => String(c.accessor))
                  .filter((a) => a && a !== idKey)}
              />
            ) : (
              perms.canCreate && (
                <Button size="sm" onClick={() => navigate(`${basePath}/new`)}>
                  <Plus className="h-3.5 w-3.5" /> {newLabel ?? 'New'}
                </Button>
              )
            )}
          </div>
        }
      />
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete record"
        message={confirmDelete ? `Remove "${String(confirmDelete[labelKey] ?? '')}"?` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
    </div>
  );
}

/**
 * Convert a panel route slug to a JWT module name. Same convention as
 * the StandardAddDropdown helper:
 *   "/panel/sales-orders"      → "SalesOrder"
 *   "/panel/tenant-domain-maps" → "TenantDomainMap"
 *   "/panel/products"          → "Product"
 */
function inferModuleFromPath(path: string): string {
  const slug = path.replace(/\/$/, '').split('/').pop() ?? '';
  const parts = slug.split(/[-_]/).filter(Boolean);
  const last = parts[parts.length - 1] ?? '';
  if (parts.length > 0 && last.endsWith('s') && last.length > 2) {
    parts[parts.length - 1] = last.slice(0, -1);
  }
  return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1).toLowerCase()).join('');
}

export interface CrudFormProps<T extends object, Create, Filter, F extends object> {
  title: string;
  service: CrudService<T, Create, Filter>;
  idKey: keyof T & string;
  basePath: string;
  emptyForm: F;
  fields: CrudField<F>[];
  /** Map server entity → form state */
  fromEntity: (entity: T) => F;
  /**
   * Map form state → entity body. May return sync OR async — async
   * lets the form `await` an image upload (or any other side-effect)
   * before serialising.
   */
  toEntity: (form: F, id?: string) => T | Promise<T>;
  /** Runs after form state is initialized, returns validation error or null */
  validate?: (form: F) => string | null;
  /**
   * Optional partial overlay applied on top of `emptyForm` for the
   * initial mount when NOT editing an existing record. Used by callers
   * that need to pre-populate fields from route/query state (e.g. the
   * Payment form landed at with `?invoiceId=…&customerId=…&amount=…`
   * from the Pending Receivables action). Ignored in edit mode — the
   * fetched entity always wins there.
   * Ref: SSO Issue #10.
   */
  initialForm?: Partial<F>;
  /**
   * Backend module name for permission checks (e.g. "Product"). When
   * omitted we infer it from `basePath` — pass explicitly when the URL
   * slug doesn't map to the backend (e.g. "tenant-domain-maps" →
   * "TenantDomainMap"). Gates the Save button by `canCreate` (create
   * mode) or `canUpdate` (edit mode). Non-privileged roles see the
   * form but cannot commit.
   */
  module?: string;
}

const inputCls = 'h-10 w-full rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';
const selectCls = 'h-10 w-full appearance-none rounded-md border border-border bg-surface-card px-3 text-sm text-content-primary outline-none focus:border-primary';

export function CrudForm<T extends object, Create, Filter, F extends object>(
  props: CrudFormProps<T, Create, Filter, F>
) {
  const { title, service, idKey, basePath, emptyForm, fields, fromEntity, toEntity, validate, initialForm, module } = props;
  const { id: rawId } = useParams<{ id?: string }>();
  // The list page navigates with `encodeId(uuid)` so the URL never
  // exposes the raw primary key. Decode here to recover the real id
  // for service.get / service.update.
  const id = decodeId(rawId);
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  const isEdit = !!id && id !== 'new';
  const moduleName = module ?? inferModuleFromPath(basePath);
  const perms = useModulePerms(moduleName);
  // Save-button gate. Edit mode requires `canUpdate`; create mode
  // requires `canCreate`. Admin bypass in `useModulePerms` means the
  // seeded admin roles always resolve true. Non-privileged roles still
  // see the form (read-only view) but can't commit.
  const canSave = isEdit ? perms.canUpdate : perms.canCreate;

  const [form, setForm] = useState<F>(() =>
    initialForm ? ({ ...emptyForm, ...initialForm } as F) : emptyForm,
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let c = false;
    async function run() {
      if (isEdit && id) {
        setLoading(true); setErr(null);
        try { const rec = await service.get(id); if (!c) setForm(fromEntity(rec)); }
        catch (e) { const msg = e instanceof Error ? e.message : 'Not found'; setErr(msg); toastRef.current.error('Load failed', msg); }
        finally { if (!c) setLoading(false); }
      } else {
        // Fresh "new" mount — apply any query-driven initialForm overlay
        // on top of the caller's `emptyForm` template.
        setForm(initialForm ? ({ ...emptyForm, ...initialForm } as F) : emptyForm);
      }
    }
    run(); return () => { c = true; };
  }, [id, isEdit, service, fromEntity, emptyForm, initialForm]);

  // Index fields by key so setField can find the field's `onChange`
  // hook without a linear scan on every keystroke.
  const fieldsByKey = useMemo(() => {
    const m = new Map<string, CrudField<F>>();
    for (const f of fields) m.set(f.key, f);
    return m;
  }, [fields]);

  /* Validation state. `touched` gates error display so a message never
     appears before the user has left the field; `submitAttempted`
     overrides it, because after a blocked save every problem has to be
     visible or the disabled button looks broken. */
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  /** Rules + sanitiser per field, rebuilt only when the field list changes. */
  const validationByKey = useMemo(() => {
    const m = new Map<string, { rules: Validator[]; sanitize?: Sanitizer }>();
    for (const f of fields) m.set(f.key, { rules: rulesFor(f), sanitize: sanitizerFor(f) });
    return m;
  }, [fields]);

  /** Live error map over current values — drives both display and the save gate. */
  const fieldErrors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const f of fields) {
      const cfg = validationByKey.get(f.key);
      if (!cfg?.rules.length) continue;
      const err = v.runRules((form as Record<string, unknown>)[f.key], cfg.rules);
      if (err) out[f.key] = err;
    }
    return out;
  }, [form, fields, validationByKey]);

  const hasErrors = Object.keys(fieldErrors).length > 0;
  const errorFor = (k: string): string | undefined =>
    submitAttempted || touched[k] ? fieldErrors[k] : undefined;

  const setField = (k: keyof F & string, val: F[keyof F & string]) =>
    setForm((prev) => {
      // Sanitise strings only — booleans (status) and File/URL values
      // from the image picker pass straight through. Because this sits
      // in the single `onChange` path, a PASTED value is reshaped
      // identically to a typed one.
      const cfg = validationByKey.get(k);
      const clean =
        cfg?.sanitize && typeof val === 'string'
          ? (cfg.sanitize(val) as F[keyof F & string])
          : val;
      const next = { ...prev, [k]: clean } as F;
      const fld = fieldsByKey.get(k);
      if (fld?.onChange) {
        const patch = fld.onChange(clean, next);
        if (patch) return { ...next, ...patch } as F;
      }
      return next;
    });

  const markTouched = (k: string) =>
    setTouched((prev) => (prev[k] ? prev : { ...prev, [k]: true }));

  const save = async () => {
    // Reveal every field-level error before the caller's own validate()
    // runs, so a blocked save always points at the offending input
    // rather than only raising a toast.
    setSubmitAttempted(true);
    if (hasErrors) {
      const first = fields.find((f) => fieldErrors[f.key]);
      toast.error('Fix errors', first ? fieldErrors[first.key] : 'Some fields are invalid.');
      return;
    }
    const e = validate ? validate(form) : null;
    if (e) { toast.error('Fix errors', e); return; }
    setSaving(true);
    try {
      // Allow toEntity to be async so callers can await image
       // uploads or other side-effects before the save fires.
      const body = await Promise.resolve(toEntity(form, isEdit ? id : undefined));
      if (isEdit) await service.update([body]);
      else await service.create([body as unknown as Create]);
      toast.success('Saved', 'Record saved');
      navigate(basePath);
    } catch (e2) { toast.error('Save failed', e2 instanceof Error ? e2.message : 'Failed'); }
    finally { setSaving(false); }
  };

  const renderField = (fld: CrudField<F>) => {
    const val = form[fld.key];
    const set = (v: F[keyof F & string]) => setField(fld.key, v);
    const fieldErr = errorFor(fld.key);
    /* Red ring on the offending control. Appended to the shared class
       string rather than replacing it, so the existing layout, spacing
       and dark-mode tokens are untouched — the audit brief rules out
       UI changes, and this is purely an error affordance. */
    const errCls = fieldErr ? ' border-danger focus:border-danger focus:ring-danger' : '';
    const inputClsV = inputCls + errCls;
    const selectClsV = selectCls + errCls;
    const blur = () => markTouched(fld.key);
    let input: ReactNode;
    if (fld.render) input = fld.render(val, set, form);
    else if (fld.type === 'textarea') input = <textarea value={String(val ?? '')} onChange={(e) => set(e.target.value as F[keyof F & string])} onBlur={blur} rows={3} placeholder={fld.placeholder} className={`${inputClsV} h-auto py-2`} />;
    else if (fld.type === 'select') input = (
      <select value={String(val ?? '')} onChange={(e) => set(e.target.value as F[keyof F & string])} onBlur={blur} className={selectClsV}>
        <option value="">—</option>
        {fld.options?.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    );
    else if (fld.type === 'status') input = (
      <select value={val ? 'active' : 'inactive'} onChange={(e) => set((e.target.value === 'active') as F[keyof F & string])} className={selectCls}>
        <option value="active">active</option><option value="inactive">inactive</option>
      </select>
    );
    else if (fld.type === 'search') {
      // Special-case: SearchableSelect owns its own label, so return it directly
      return (
        <div key={fld.key} className={fld.fullWidth ? 'md:col-span-2' : ''}>
          <SearchableSelect
            label={`${fld.label}${fld.required ? ' *' : ''}`}
            value={String(val ?? '')}
            onChange={(v) => set(v as F[keyof F & string])}
            loadOptions={fld.loadOptions}
            options={fld.options as SearchableSelectOption[] | undefined}
            placeholder={fld.placeholder ?? 'Select…'}
            searchPlaceholder="Type to search…"
            allowCustom={fld.allowCustom}
          />
          {fieldErr && <span className="mt-1 block text-xs text-danger">{fieldErr}</span>}
        </div>
      );
    }
    else if (fld.type === 'image') {
      // Image picker (URL + Upload + Preview). Owns its own label so
      // we return early like the search variant.
      return (
        <div key={fld.key} className={fld.fullWidth ? 'md:col-span-2' : ''}>
          <ImagePicker
            label={`${fld.label}${fld.required ? ' *' : ''}`}
            value={String(val ?? '')}
            onChange={(v) => set(v as F[keyof F & string])}
            hint={fld.placeholder}
          />
          {fieldErr && <span className="mt-1 block text-xs text-danger">{fieldErr}</span>}
        </div>
      );
    }
    else if (fld.type === 'number') input = (
      // Clears the leading `0` on focus so the user can type
      // immediately (was: had to backspace the zero first). See
      // `apps/panel/src/components/NumberField.tsx`.
      <NumberField
        step="0.01"
        value={Number(val ?? 0)}
        onValueChange={(n) => set(n as F[keyof F & string])}
        placeholder={fld.placeholder}
        onBlur={blur}
        className={inputClsV}
      />
    );
    else if (fld.type === 'tel') {
      /* Phone / mobile inputs are numeric-only — strip every
         non-digit at the input boundary so users physically cannot
         type alphabets. Pasting "+91 98765-43210" still works (it
         normalises to "919876543210"). Bug ref: AI Agent (round 3) —
         "phone accept string value it should be only number allows". */
      input = (
        <input
          type="tel"
          inputMode="numeric"
          pattern="\d{10,15}"
          maxLength={15}
          value={String(val ?? '')}
          onChange={(e) =>
            set(e.target.value.replace(/\D+/g, '').slice(0, 15) as F[keyof F & string])
          }
          onBlur={blur}
          placeholder={fld.placeholder}
          className={inputClsV}
        />
      );
    }
    else if (fld.type === 'date') input = <input type="date" value={String(val ?? '')} onChange={(e) => set(e.target.value as F[keyof F & string])} onBlur={blur} className={inputClsV} />;
    else if (fld.type === 'time') {
      // 24-hour clock picker. Browser-native input gives a clock UI;
      // value is always `HH:MM` (no seconds). The form's `toEntity`
      // is responsible for appending `:00` to match a TimeSpan column
      // when the backend wants `HH:MM:SS`.
      input = (
        <input
          type="time"
          step={60}
          value={String(val ?? '').slice(0, 5)}
          onChange={(e) => set(e.target.value as F[keyof F & string])}
          onBlur={blur}
          placeholder={fld.placeholder ?? 'HH:MM'}
          className={inputClsV}
        />
      );
    }
    else input = <input type={fld.type ?? 'text'} value={String(val ?? '')} onChange={(e) => set(e.target.value as F[keyof F & string])} onBlur={blur} placeholder={fld.placeholder} className={inputClsV} />;
    return (
      <label key={fld.key} className={`flex flex-col gap-1 ${fld.fullWidth ? 'md:col-span-2' : ''}`}>
        <span className="text-xs font-medium text-content-secondary">{fld.label} {fld.required && <span className="text-danger">*</span>}</span>
        {input}
        {fieldErr && <span className="text-xs text-danger">{fieldErr}</span>}
      </label>
    );
  };

  return (
    <div className="-m-6 p-[3px]">
      {err && <Alert variant="danger" className="mb-3">{err}</Alert>}
      <Card>
        <div className="mb-3 flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate(basePath)}><ArrowLeft className="h-4 w-4" /></Button>
          <h2 className="text-base font-semibold text-content-primary">{isEdit ? `Edit ${title.toLowerCase()}` : `New ${title.toLowerCase()}`}</h2>
        </div>
        {loading ? <div className="text-sm text-content-muted">Loading…</div> : (
          <div className="grid gap-3 md:grid-cols-2">
            {fields.map(renderField)}
          </div>
        )}
        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate(basePath)}>Cancel</Button>
          {/* Save is disabled once a blocked save has revealed the errors,
              not before — greying it out on a pristine form gives the user
              nothing to act on and reads as a broken screen. The first
              click validates and surfaces every message; from then on the
              button stays disabled until the form is clean. */}
          {canSave && (
            <Button
              onClick={save}
              disabled={saving || loading || (submitAttempted && hasErrors)}
              title={submitAttempted && hasErrors ? 'Fix the highlighted fields first' : undefined}
            >
              <Save className="h-4 w-4" /> {saving ? 'Saving…' : isEdit ? 'Update' : 'Save'}
            </Button>
          )}
          {!canSave && (
            <Button variant="outline" disabled title={`You don't have permission to ${isEdit ? 'update' : 'create'} ${title.toLowerCase()}`}>
              <Save className="h-4 w-4" /> {isEdit ? 'Update' : 'Save'}
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
}
